'use strict';

const { getBsv, randomBytes } = require('./lib');
const { toB64, fromB64 } = require('./b64');
const { KDF, deriveKeyMaterial } = require('./kdf');
const { encryptVault, decryptVault } = require('./vault');
const { PATHS, deriveRoleKeys } = require('./paths');
const didlib = require('./did');
const vcjwt = require('./vcjwt');

// Derive the three role keys (identity/finance/tokensOrdinals) from a mnemonic phrase.
// The IDENTITY key (secp256k1) doubles as the DID/VC signing key — so the entire
// identity is rooted in the mnemonic and fully recoverable from it alone (no separate
// random key to vault or recover).
function openKeysFromMnemonic(phrase) {
  const bsv = getBsv();
  const hd = bsv.Mnemonic.fromString(phrase).toHDPrivateKey('', bsv.Networks.mainnet);
  const role = deriveRoleKeys(hd);
  const view = (k, path) => ({
    privateKey: k,
    address: k.toAddress().toString(),
    pubkey: k.toPublicKey().toString(),
    path,
  });
  return {
    identity: view(role.identity, PATHS.identity),
    finance: view(role.finance, PATHS.finance),
    tokensOrdinals: view(role.tokensOrdinals, PATHS.tokensOrdinals),
  };
}

// Build the public identity records (DID doc + JWKS) from the signing (identity) key.
function publicIdentity(keys, did, kid = 'key-1') {
  const publicJwk = vcjwt.jwkFromPublicKey(keys.identity.privateKey.toPublicKey());
  return {
    publicJwk,
    didDocument: didlib.buildDidDocument(did, publicJwk, kid),
    jwks: didlib.buildJwks(publicJwk, kid),
  };
}

/**
 * Create a brand-new non-custodial identity from a password. Everything is client-side.
 * Returns { phrase, did, handle, jwks, keys, registration }. The vault now stores only
 * the mnemonic (+ did/handle for convenience) — the signing key is derived, not stored.
 */
async function createIdentity(password, opts = {}) {
  const bsv = getBsv();

  const phrase = bsv.Mnemonic.fromRandom().toString();
  const keys = openKeysFromMnemonic(phrase);

  const handle = await didlib.deriveHandle(keys.identity.pubkey);
  const did = didlib.buildDid(handle, opts.domain);
  const { didDocument, jwks } = publicIdentity(keys, did);

  const salt = randomBytes(16);
  const { authVerifier, vaultKeyBytes } = await deriveKeyMaterial(password, salt);
  const vaultPlain = { v: 2, mnemonic: phrase, did, handle };
  const vaultBlob = await encryptVault(vaultKeyBytes, vaultPlain);

  return {
    phrase,
    did,
    handle,
    jwks,
    keys,
    registration: {
      salt: toB64(salt),
      kdf: KDF,
      authVerifier: toB64(authVerifier),
      vaultBlob,
      did,
      handle,
      didDocument,
      jwks,
      identityAddress: keys.identity.address,
    },
  };
}

/**
 * Unlock an existing identity on ANY device: password + the server-stored
 * { salt, vaultBlob } -> live keys. Throws on wrong password (GCM tag failure).
 */
async function unlockIdentity(password, stored) {
  const salt = fromB64(stored.salt);
  const { vaultKeyBytes } = await deriveKeyMaterial(password, salt);
  const vault = await decryptVault(vaultKeyBytes, stored.vaultBlob); // throws if wrong
  const keys = openKeysFromMnemonic(vault.mnemonic);
  const handle = vault.handle || (await didlib.deriveHandle(keys.identity.pubkey));
  const did = vault.did || didlib.buildDid(handle);
  const { jwks } = publicIdentity(keys, did);
  return { mnemonic: vault.mnemonic, did, handle, jwks, keys };
}

/** Rebuild a full identity directly from a recovered mnemonic (used by recovery). */
async function identityFromMnemonic(phrase) {
  const keys = openKeysFromMnemonic(phrase);
  const handle = await didlib.deriveHandle(keys.identity.pubkey);
  const did = didlib.buildDid(handle);
  const { jwks } = publicIdentity(keys, did);
  return { mnemonic: phrase, did, handle, jwks, keys };
}

/**
 * Compute only the authVerifier (for login), without needing the vault blob.
 */
async function computeAuthVerifier(password, saltB64) {
  const { authVerifier } = await deriveKeyMaterial(password, fromB64(saltB64));
  return toB64(authVerifier);
}

module.exports = { createIdentity, unlockIdentity, computeAuthVerifier, openKeysFromMnemonic, identityFromMnemonic };
